import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Box, GlobalStyles } from '@mui/material';
import { keyframes } from '@emotion/react';

import Header from './components/Header';
import Home from './pages/Home';
import Exhibits from './pages/Exhibits';
import Visit from './pages/Visit';
// import Events from './pages/Events'; // Temporarily disabled
import Membership from './pages/Membership';
import Volunteer from './pages/Volunteer';
import ConfirmSubscription from './pages/ConfirmSubscription';
import About from './pages/About';
import ContactPage from './pages/Contact';
import Location from './pages/Location';
import Footer from './components/Footer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Archives from './pages/Archives';
import Donate from './pages/Donate';
import OpenHouseRSVP from './pages/OpenHouseRSVP';
import Links from './pages/Links'; // Affiliates / Links page
import NewsPage from './pages/NewsPage'; // NEW

// NEW: admin app
import AdminApp from './admin/AdminApp';

/** Simple 3‑dot bubbles animation */
const pulse = keyframes`
    0%   { transform: scale(1);   opacity: 1; }
    50%  { transform: scale(.7);  opacity: .55; }
    100% { transform: scale(1);   opacity: 1; }
`;

/** White overlay shown briefly on any route change */
function NavigationOverlay({ show }) {
    if (!show) return null;
    return (
        <Box
            role="presentation"
            aria-hidden
            sx={{
                position: 'fixed',
                inset: 0,
                bgcolor: '#fff',
                zIndex: 20000,
                display: 'grid',
                placeItems: 'center',
            }}
        >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                {[0, 1, 2].map((i) => (
                    <Box
                        key={i}
                        sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: '#002F6C',
                            animation: `${pulse} 1s ease-in-out ${i * 0.15}s infinite`,
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

function PublicRoutes() {
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/exhibits" element={<Exhibits />} />
            <Route path="/news" element={<NewsPage />} /> {/* NEW */}
            <Route path="/visit" element={<Visit />} />
            {/* Events page temporarily disabled — redirect to home */}
            <Route path="/events" element={<Navigate to="/" replace />} />
            <Route path="/volunteer" element={<Volunteer />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/newsletter/confirm" element={<ConfirmSubscription />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacyPolicy" element={<PrivacyPolicy />} />
            <Route path="/location" element={<Location />} />
            <Route path="/archives" element={<Archives />} />
            <Route path="/links" element={<Links />} />
            <Route path="/admin/*" element={<AdminApp />} />
            <Route path="/rsvp" element={<OpenHouseRSVP />} />
        </Routes>
    );
}

export default function App() {
    const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';
    const CURRENCY = process.env.REACT_APP_PAYPAL_CURRENCY || 'USD';
    const location = useLocation();

    const isAdmin = location.pathname.startsWith('/admin');

    // brief overlay + scroll-to-top on *any* route change
    const [transitioning, setTransitioning] = useState(false);
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTransitioning(true);
        const t = setTimeout(() => setTransitioning(false), 450);
        return () => clearTimeout(t);
    }, [location.pathname, location.search]);

    // also show the overlay immediately when any internal <a> is clicked
    useEffect(() => {
        const onClick = (e) => {
            let el = e.target;
            while (el && el !== document.body) {
                if (el.tagName === 'A') {
                    const href = el.getAttribute('href');
                    const target = el.getAttribute('target');
                    if (href && !href.startsWith('http') && target !== '_blank') {
                        setTransitioning(true);
                    }
                    break;
                }
                el = el.parentElement;
            }
        };
        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, []);

    return (
        <PayPalScriptProvider
            options={{
                'client-id': CLIENT_ID,
                currency: CURRENCY,
                intent: 'capture',
            }}
        >
            {!isAdmin && <Header />}

            {/* Global polish + targeted fixes */}
            <GlobalStyles
                styles={{
                    /* 1) Make small inputs readable on phones only */
                    '@media (max-width:600px)': {
                        '.MuiInputBase-input, textarea': { fontSize: '0.95rem' },
                    },

                    /* 2) HERO CTA: improve clickability & tap targets */
                    /* Increase minimum hit area for all Material buttons */
                    '.MuiButtonBase-root': {
                        minHeight: 44,
                        minWidth: 44,
                    },
                    /* Ensure hero CTAs sit above any overlays and accept clicks */
                    '.home-hero .MuiButtonBase-root, .hero .MuiButtonBase-root': {
                        position: 'relative',
                        zIndex: 5,
                        pointerEvents: 'auto',
                    },
                    /* If a hero overlay exists, don't let it eat clicks */
                    '.home-hero [data-overlay], .hero [data-overlay], .home-hero .hero-overlay, .hero .hero-overlay':
                        {
                            pointerEvents: 'none',
                        },

                    /* 3) PAYPAL WALLET / CARD FIELDS: spacing & field height
                       These selectors purposely target common PayPal containers
                       without relying on a specific implementation detail.
                    */
                    '[id^="paypal"], [class^="paypal"], [class*=" paypal"]': {
                        '--pp-gap': '12px',
                    },
                    /* Space under the wallet button row */
                    '[id^="paypal"] .paypal-button-container, [class^="paypal"] .paypal-button-container, [class*=" paypal"] .paypal-button-container':
                        {
                            marginBottom: 16,
                        },
                    /* Make generated forms use a clean grid gap */
                    '[id^="paypal"] form, [class^="paypal"] form, [class*=" paypal"] form, [id^="paypal"] .card-fields, [class^="paypal"] .card-fields, [class*=" paypal"] .card-fields':
                        {
                            display: 'grid',
                            gap: 'var(--pp-gap)',
                        },
                    /* Comfortable control height for visible (non-iframe) inputs */
                    '[id^="paypal"] input, [class^="paypal"] input, [class*=" paypal"] input, [id^="paypal"] select, [class^="paypal"] select, [class*=" paypal"] select':
                        {
                            height: 44,
                        },
                }}
            />

            {isAdmin ? <AdminApp /> : <PublicRoutes />}

            {!isAdmin && <Footer />}
            <NavigationOverlay show={transitioning} />
        </PayPalScriptProvider>
    );
}
